/**
 * Operations route group.
 *
 * Smart panel, rewards, insights, journey templates, profile traits, branch
 * banking, audit trail, general ledger, payment operations, payments hub, GL
 * enrichment and KYC routes.
 *
 * Extracted verbatim from AppRoutes.tsx. The declaration sequence is preserved
 * exactly as part of the route structural contract; see
 * `__tests__/AppRoutes.structure.test.tsx`.
 */

import { lazy } from 'react';
import { Route } from 'react-router-dom';
import { ProtectedRoute } from '../../auth/components/ProtectedRoute';
import { ROUTES, permissionsFor } from '../routeCatalogue';

// ---------------------------------------------------------------------------
// Lazy-loaded route pages
// ---------------------------------------------------------------------------

const SmartPanelMessages = lazy(() => import('../../features/smart-panel/pages/SmartPanelMessages'));
const MessageEditor = lazy(() => import('../../features/smart-panel/pages/MessageEditor'));
const RewardsConfig = lazy(() => import('../../features/rewards/pages/RewardsConfig'));
const RewardsBadges = lazy(() => import('../../features/rewards/pages/RewardsBadges'));
const RewardsTiers = lazy(() => import('../../features/rewards/pages/RewardsTiers'));
const RewardsOffers = lazy(() => import('../../features/rewards/pages/RewardsOffers'));
const RewardsAnalytics = lazy(() => import('../../features/rewards/pages/RewardsAnalytics'));
const InsightsManager = lazy(() => import('../../features/insights/pages/InsightsManager'));
const TemplateList = lazy(() => import('../../features/journeys-admin/pages/TemplateList'));
const TemplateEditor = lazy(() => import('../../features/journeys-admin/pages/TemplateEditor'));
const TraitsManager = lazy(() => import('../../features/profile-traits/pages/TraitsManager'));
const BranchDashboard = lazy(() => import('../../features/branch-banking/pages/BranchDashboard'));
const CustomerLookup = lazy(() => import('../../features/branch-banking/pages/CustomerLookup'));
const AccountOpening = lazy(() => import('../../features/branch-banking/pages/AccountOpening'));
const BranchTransactions = lazy(() => import('../../features/branch-banking/pages/Transactions'));
const CashManagement = lazy(() => import('../../features/branch-banking/pages/CashManagement'));
const TellerQueue = lazy(() => import('../../features/branch-banking/pages/TellerQueue'));
const AuditDashboard = lazy(() => import('../../features/audit-trail/pages/AuditDashboard'));
const AuditLog = lazy(() => import('../../features/audit-trail/pages/AuditLog'));
const AuditUserActivity = lazy(() => import('../../features/audit-trail/pages/UserActivity'));
const AuditReportBuilder = lazy(() => import('../../features/audit-trail/pages/ReportBuilder'));
const GLDashboard = lazy(() => import('../../features/general-ledger/pages/GLDashboard'));
const ChartOfAccounts = lazy(() => import('../../features/general-ledger/pages/ChartOfAccounts'));
const JournalEntries = lazy(() => import('../../features/general-ledger/pages/JournalEntries'));
const JournalDetail = lazy(() => import('../../features/general-ledger/pages/JournalDetail'));
const TrialBalance = lazy(() => import('../../features/general-ledger/pages/TrialBalance'));
const PaymentsDashboard = lazy(() => import('../../features/payment-ops/pages/PaymentsDashboard'));
const PaymentSearch = lazy(() => import('../../features/payment-ops/pages/PaymentSearch'));
const PaymentOpsReconciliation = lazy(() => import('../../features/payment-ops/pages/Reconciliation'));
const PaymentOpsStandingOrders = lazy(() => import('../../features/payment-ops/pages/StandingOrders'));
const PaymentOpsFailedPayments = lazy(() => import('../../features/payment-ops/pages/FailedPayments'));
const PaymentOpsSchemeRouting = lazy(() => import('../../features/payment-ops/pages/SchemeRouting'));
const AdminPaymentList = lazy(() => import('../../pages/payments/PaymentList'));
const AdminPaymentDetail = lazy(() => import('../../pages/payments/PaymentDetail'));
const AdminInitiatePayment = lazy(() => import('../../pages/payments/InitiatePayment'));
const AdminPaymentSchemes = lazy(() => import('../../pages/payments/PaymentSchemes'));
const AdminStandingOrders = lazy(() => import('../../pages/payments/StandingOrders'));
const AdminDirectDebits = lazy(() => import('../../pages/payments/DirectDebits'));
const AdminChartOfAccounts = lazy(() => import('../../pages/gl/ChartOfAccounts'));
const AdminJournalEntries = lazy(() => import('../../pages/gl/JournalEntries'));
const AdminTrialBalance = lazy(() => import('../../pages/gl/TrialBalance'));
const AdminFinancialStatements = lazy(() => import('../../pages/gl/FinancialStatements'));
const AdminCostCentres = lazy(() => import('../../pages/gl/CostCentres'));
const AdminReconciliation = lazy(() => import('../../pages/gl/Reconciliation'));
const AdminPeriodManagement = lazy(() => import('../../pages/gl/PeriodManagement'));
const KycDashboard = lazy(() => import('../../features/kyc/pages/KycDashboard'));
const KycAnalytics = lazy(() => import('../../features/kyc/pages/KycAnalytics'));
const KycReviews = lazy(() => import('../../features/kyc/pages/KycReviews'));

// ---------------------------------------------------------------------------
// operationsRoutes()
// ---------------------------------------------------------------------------

export function operationsRoutes() {
    return (
        <>
        {/* Smart Panel Routes */}
        <Route path={ROUTES.SMART_PANEL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.SMART_PANEL)}>
                <SmartPanelMessages />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.SMART_PANEL_CREATE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.SMART_PANEL_CREATE)}>
                <MessageEditor />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.SMART_PANEL_DETAIL_EDIT.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.SMART_PANEL_DETAIL_EDIT)}>
                <MessageEditor />
            </ProtectedRoute>
        } />

        {/* Rewards Routes */}
        <Route path={ROUTES.REWARDS_CONFIG.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.REWARDS_CONFIG)}>
                <RewardsConfig />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.REWARDS_BADGES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.REWARDS_BADGES)}>
                <RewardsBadges />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.REWARDS_TIERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.REWARDS_TIERS)}>
                <RewardsTiers />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.REWARDS_OFFERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.REWARDS_OFFERS)}>
                <RewardsOffers />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.REWARDS_ANALYTICS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.REWARDS_ANALYTICS)}>
                <RewardsAnalytics />
            </ProtectedRoute>
        } />

        {/* Insights Routes */}
        <Route path={ROUTES.INSIGHTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.INSIGHTS)}>
                <InsightsManager />
            </ProtectedRoute>
        } />

        {/* Journey Templates Routes */}
        <Route path={ROUTES.JOURNEY_TEMPLATES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.JOURNEY_TEMPLATES)}>
                <TemplateList />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.JOURNEY_TEMPLATES_NEW.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.JOURNEY_TEMPLATES_NEW)}>
                <TemplateEditor />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.JOURNEY_TEMPLATES_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.JOURNEY_TEMPLATES_DETAIL)}>
                <TemplateEditor />
            </ProtectedRoute>
        } />

        {/* Profile Traits Routes */}
        <Route path={ROUTES.PROFILE_TRAITS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PROFILE_TRAITS)}>
                <TraitsManager />
            </ProtectedRoute>
        } />

        {/* Branch Banking Routes */}
        <Route path={ROUTES.BRANCH_DASHBOARD.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_DASHBOARD)}>
                <BranchDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_CUSTOMERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_CUSTOMERS)}>
                <CustomerLookup />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_ACCOUNTS_OPEN.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_ACCOUNTS_OPEN)}>
                <AccountOpening />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_TRANSACTIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_TRANSACTIONS)}>
                <BranchTransactions />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_CASH.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_CASH)}>
                <CashManagement />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_QUEUE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_QUEUE)}>
                <TellerQueue />
            </ProtectedRoute>
        } />

        {/* Audit Trail Routes */}
        <Route path={ROUTES.AUDIT.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.AUDIT)}>
                <AuditDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.AUDIT_LOG.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.AUDIT_LOG)}>
                <AuditLog />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.AUDIT_USERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.AUDIT_USERS)}>
                <AuditUserActivity />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.AUDIT_REPORT_BUILDER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.AUDIT_REPORT_BUILDER)}>
                <AuditReportBuilder />
            </ProtectedRoute>
        } />

        {/* General Ledger Routes */}
        <Route path={ROUTES.GENERAL_LEDGER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GENERAL_LEDGER)}>
                <GLDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GENERAL_LEDGER_ACCOUNTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GENERAL_LEDGER_ACCOUNTS)}>
                <ChartOfAccounts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GENERAL_LEDGER_JOURNALS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GENERAL_LEDGER_JOURNALS)}>
                <JournalEntries />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GENERAL_LEDGER_JOURNALS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GENERAL_LEDGER_JOURNALS_DETAIL)}>
                <JournalDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GENERAL_LEDGER_TRIAL_BALANCE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GENERAL_LEDGER_TRIAL_BALANCE)}>
                <TrialBalance />
            </ProtectedRoute>
        } />

        {/* Payment Operations Routes */}
        <Route path={ROUTES.PAYMENT_OPS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENT_OPS)}>
                <PaymentsDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENT_OPS_SEARCH.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENT_OPS_SEARCH)}>
                <PaymentSearch />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENT_OPS_RECONCILIATION.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENT_OPS_RECONCILIATION)}>
                <PaymentOpsReconciliation />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENT_OPS_STANDING_ORDERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENT_OPS_STANDING_ORDERS)}>
                <PaymentOpsStandingOrders />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENT_OPS_FAILED.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENT_OPS_FAILED)}>
                <PaymentOpsFailedPayments />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENT_OPS_SCHEME_ROUTING.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENT_OPS_SCHEME_ROUTING)}>
                <PaymentOpsSchemeRouting />
            </ProtectedRoute>
        } />

        {/* Payments Hub Routes (9C.7) */}
        <Route path={ROUTES.PAYMENTS_HUB.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENTS_HUB)}>
                <AdminPaymentList />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENTS_HUB_INITIATE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENTS_HUB_INITIATE)}>
                <AdminInitiatePayment />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENTS_HUB_SCHEMES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENTS_HUB_SCHEMES)}>
                <AdminPaymentSchemes />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENTS_HUB_STANDING_ORDERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENTS_HUB_STANDING_ORDERS)}>
                <AdminStandingOrders />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENTS_HUB_DIRECT_DEBITS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENTS_HUB_DIRECT_DEBITS)}>
                <AdminDirectDebits />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PAYMENTS_HUB_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENTS_HUB_DETAIL)}>
                <AdminPaymentDetail />
            </ProtectedRoute>
        } />

        {/* GL Enrichment Routes (9C.8) */}
        <Route path={ROUTES.GL_ACCOUNTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GL_ACCOUNTS)}>
                <AdminChartOfAccounts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GL_JOURNALS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GL_JOURNALS)}>
                <AdminJournalEntries />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GL_TRIAL_BALANCE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GL_TRIAL_BALANCE)}>
                <AdminTrialBalance />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GL_FINANCIAL_STATEMENTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GL_FINANCIAL_STATEMENTS)}>
                <AdminFinancialStatements />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GL_COST_CENTRES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GL_COST_CENTRES)}>
                <AdminCostCentres />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GL_RECONCILIATION.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GL_RECONCILIATION)}>
                <AdminReconciliation />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GL_PERIODS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GL_PERIODS)}>
                <AdminPeriodManagement />
            </ProtectedRoute>
        } />

        {/* KYC Management Routes */}
        <Route path={ROUTES.KYC.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYC)}>
                <KycDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.KYC_REVIEWS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYC_REVIEWS)}>
                <KycReviews />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.KYC_ANALYTICS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYC_ANALYTICS)}>
                <KycAnalytics />
            </ProtectedRoute>
        } />
        </>
    );
}
